import { useMemo } from 'react';
import { useBrainStore } from '@/state/brainStore';
import { useSimulationStore } from '@/state/simulationStore';
import DashboardKPITile from '@/components/DashboardKPITile';
import InfoTip from '@/components/InfoTip';
import { ReportBarChart, ReportLineChart } from '@/components/reporting/ReportChart';
import { aud } from '@/utils/format';
import {
  reportingWeekDays,
  revenueByStore,
  daysInRange,
  dayKeyOf,
} from '@/domain/reportingEngine';
import { networkRevenueByWeek } from '@/domain/ownerRevenue';
import { AlertTriangle, Sparkles, TrendingUp } from 'lucide-react';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** '2026-04-27' -> '27 Apr'. Parsed from the day-key so there is no timezone drift. */
function shortDay(dayIso: string): string {
  const [, m, d] = dayIso.split('-');
  return `${Number(d)} ${MONTHS[Number(m) - 1]}`;
}

export default function OwnerDashboard() {
  const customers = useBrainStore((s) => s.customers);
  const studios = useBrainStore((s) => s.studios);
  const bookings = useBrainStore((s) => s.bookings);
  const salePayments = useBrainStore((s) => s.salePayments);
  const artists = useBrainStore((s) => s.artists);
  const stats = useBrainStore((s) => s.todayStats);
  const clockIso = useSimulationStore((s) => s.clockIso);

  // Live revenue - read straight off the sale ledger via the commission engine.
  // Studios/bookings/sales/artists are in the deps so the numbers move the moment
  // a sale lands.
  const { shopRows, weekTotal, weekOurs, weekArtist, mtdTotal, weekly } = useMemo(() => {
    const week = reportingWeekDays(clockIso);
    const byShop = revenueByStore(week);
    const today = dayKeyOf(clockIso);
    const monthStart = `${today.slice(0, 8)}01`;
    const mtd = revenueByStore(daysInRange(monthStart, today));
    return {
      shopRows: byShop.filter((r) => r.totalAud > 0),
      weekTotal: byShop.reduce((a, r) => a + r.totalAud, 0),
      weekOurs: byShop.reduce((a, r) => a + r.oursAud, 0),
      weekArtist: byShop.reduce((a, r) => a + r.artistAud, 0),
      mtdTotal: mtd.reduce((a, r) => a + r.totalAud, 0),
      weekly: networkRevenueByWeek(clockIso, 8),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clockIso, studios, bookings, salePayments, artists]);

  const takePct = weekTotal > 0 ? Math.round((weekOurs / weekTotal) * 100) : 0;
  const highValue = Object.values(customers).filter((c) => c.leadScore >= 90);

  const barData = shopRows.map((r) => ({ shop: r.name, take: r.oursAud, artist: r.artistAud }));
  const lineData = weekly.map((w, i) => ({
    week: i === weekly.length - 1 ? 'This wk' : shortDay(w.weekEndIso),
    revenue: w.totalAud,
  }));

  return (
    <section className="px-8 py-8" data-testid="owner-dashboard">
      <header className="max-w-7xl">
        <p className="label-small-caps text-gold-bright">Owner · Revenue</p>
        <h1 className="heading-display text-3xl text-cream mt-2">Owner Dashboard</h1>
        <p className="font-serif text-cream-soft mt-3">
          Revenue per shop and per week, studio take against artist payout. Live from the sale
          ledger.
        </p>
      </header>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-3 max-w-7xl">
        <DashboardKPITile
          label="Network revenue this week"
          value={aud(weekTotal)}
          accent
          info={
            <>
              Money taken across all studios in the last 7 days - studio take plus artist payout,
              merch excluded. Sum of every shop&apos;s completed-session revenue.
            </>
          }
        />
        <DashboardKPITile
          label="Month to date"
          value={aud(mtdTotal)}
          info={<>Revenue from the 1st of this month to today, added up across every studio.</>}
        />
        <DashboardKPITile
          label="Studio take this week"
          value={aud(weekOurs)}
          caption={`${takePct}% of revenue · artists ${aud(weekArtist)}`}
          info={
            <>
              The house&apos;s share of this week&apos;s revenue after artist commissions (via the
              commission engine). The remainder is paid out to the artists.
            </>
          }
        />
      </div>

      <div className="mt-8 grid grid-cols-12 gap-6 max-w-7xl">
        <div className="col-span-12 lg:col-span-8 space-y-6">
          <div className="panel p-5" data-testid="owner-revenue-by-shop">
            <h2 className="heading-serif text-lg text-cream mb-1 flex items-center gap-1.5">
              Revenue by shop — this week
              <InfoTip>
                Each bar is a studio&apos;s revenue for the last 7 days, split into the studio&apos;s
                take (gold) and what is paid out to artists (blue). Worked out per completed session
                by the commission engine.
              </InfoTip>
            </h2>
            {barData.length === 0 ? (
              <p className="font-serif text-cream-soft text-sm py-8 text-center">
                No completed revenue this week yet.
              </p>
            ) : (
              <ReportBarChart
                data={barData}
                xKey="shop"
                stacked
                series={[
                  { key: 'take', name: 'Studio take' },
                  { key: 'artist', name: 'Artist payout' },
                ]}
              />
            )}
          </div>

          <div className="panel p-5" data-testid="owner-weekly-revenue">
            <h2 className="heading-serif text-lg text-cream mb-1 flex items-center gap-1.5">
              Weekly revenue — network
              <InfoTip>
                Total network revenue for each of the last 8 weeks, oldest to newest. Each point is
                the 7 days ending that week - the running weekly tally.
              </InfoTip>
            </h2>
            <ReportLineChart
              data={lineData}
              xKey="week"
              hideLegend
              series={[{ key: 'revenue', name: 'Network revenue' }]}
            />
          </div>
        </div>

        <aside className="col-span-12 lg:col-span-4 space-y-4">
          <div className="panel p-4">
            <h3 className="label-small-caps text-gold-bright mb-2 flex items-center gap-2">
              AI vs human cost
              <span className="text-[9px] font-bold tracking-widest uppercase text-cream-faint border border-dashed border-gold-dim/60 rounded px-1.5 py-0.5">
                Placeholder — pending cost model
              </span>
            </h3>
            <div className="opacity-60">
              <Row label="Lead Intake AI" value={aud(2_350)} delta={-58} />
              <Row label="Human cost benchmark" value={aud(5_600)} mute />
              <Row label="Reactivation AI cost" value={aud(162.11)} delta={-72} />
              <Row label="AI voice (overflow + reactivation)" value={aud(184.40)} />
            </div>
            <p className="font-serif text-cream-soft text-xs mt-3">
              Figures illustrative — the real cost model is owed by Cam. Shown so the panel&apos;s
              place on the page is set.
            </p>
          </div>
          <div className="panel p-4">
            <h3 className="label-small-caps text-gold-bright mb-2">Growth</h3>
            <Row label="New customers this month" value="184" />
            <Row label="Retention rate" value="64%" />
            <Row label="Average booking value" value={aud(820)} />
            <Row label="LTV (12-month)" value={aud(1_620)} />
          </div>
        </aside>
      </div>

      <section className="mt-8 max-w-7xl">
        <h2 className="heading-serif text-lg text-cream mb-3">Alerts needing your hand</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {highValue.slice(0, 3).map((c) => (
            <div key={c.id} className="panel p-4 border-gold-bright/40">
              <p className="label-small-caps text-gold-bright flex items-center gap-2">
                <AlertTriangle size={12} /> High-value lead
              </p>
              <p className="font-inter text-base text-cream mt-2">
                {c.firstName} {c.lastName}
              </p>
              <p className="font-inter text-[10px] text-cream-soft uppercase tracking-widest mt-2">
                {c.designIntent?.type} · {c.designIntent?.size} · score {c.leadScore}
              </p>
            </div>
          ))}
          <div className="panel p-4">
            <p className="label-small-caps text-gold-bright flex items-center gap-2">
              <Sparkles size={12} /> Reactivation approvals
            </p>
            <p className="font-inter text-base text-cream mt-2">38 warm hand-offs ready</p>
            <p className="font-serif text-cream-soft text-xs mt-2">
              Aaron Singh + 37 others from overnight campaign. Approve in Reactivation Engine.
            </p>
          </div>
          <div className="panel p-4">
            <p className="label-small-caps text-gold-bright flex items-center gap-2">
              <TrendingUp size={12} /> Weekly revenue flag
            </p>
            <p className="font-inter text-base text-cream mt-2">
              {stats.deposits} deposits landed today across the network
            </p>
          </div>
        </div>
      </section>
    </section>
  );
}

function Row({
  label,
  value,
  delta,
  mute,
}: {
  label: string;
  value: string;
  delta?: number;
  mute?: boolean;
}) {
  return (
    <div className="flex items-center justify-between text-sm py-1.5 border-b border-gold-dim/15 last:border-0">
      <span className={mute ? 'text-cream-soft' : 'text-cream'}>{label}</span>
      <span className="flex items-center gap-2 font-mono">
        <span>{value}</span>
        {typeof delta === 'number' ? (
          <span className={delta < 0 ? 'text-mint text-[10px]' : 'text-blood text-[10px]'}>
            {delta > 0 ? '+' : ''}
            {delta}%
          </span>
        ) : null}
      </span>
    </div>
  );
}
